import { FC, useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { AssetColor, AssetSize, Heading, HeadingLevel, HeadingTag, Icon, IconName, Text, TextColor, TextTag, TextVariant } from '@enbw/ui-react'
import styles from './styles.module.scss'
import { ProfileMenu } from './components/ProfileMenu'
import { Paths } from '@/paths'
import { UserRole } from '@/application/auth/types/userRole'
import { useAuth } from '@/application/auth/hooks/useAuth'

export const AppHeader: FC = () => {
  const { t } = useTranslation()
  const auth = useAuth()
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const handleClickOnProfile = (): void => {
    setIsMenuOpen(false)
    navigate(Paths.PROFILE)
  }

  const handleClickOnLogout = (): void => {
    auth.logout()
  }

  const toggleMenu = (): void => {
    setIsMenuOpen(!isMenuOpen)
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  const displayRoles = auth.roles
    .map((role) => {
      if (role === UserRole.Reader) return t('header.roleReader')
      if (role === UserRole.Developer) return t('header.roleDeveloper')
      return role
    })
    .join(', ')

  return (
    <header className={styles.header}>
      <div className={styles.headerContent}>
        <div className={styles.appIcon}>
          <img src="/mobility_icon.svg" alt={t('common.appName')} />
        </div>
        <div className={styles.titles}>
          <RouterLink to={Paths.HOME} className={styles.appTitle}>
            <Heading level={HeadingLevel.H3} tag={HeadingTag.H1}>
              {t('common.appName')}
            </Heading>
          </RouterLink>

          <Text variant={TextVariant.BodyStandard} tag={TextTag.Span} className={styles.moduleTitle}>
            {t('header.moduleContracts')}
          </Text>
        </div>

        <div className={styles.spacer} />
        {auth.isAuthenticated && (
          <div className={styles.profile} ref={menuRef}>
            <button className={styles.profileButton} onClick={toggleMenu} aria-expanded={isMenuOpen} aria-haspopup="true">
              <span>
                <Text variant={TextVariant.BodyStandard} tag={TextTag.Span} color={TextColor.BrandPrimary}>
                  {auth.name}
                </Text>
                {displayRoles && (
                  <>
                    <Text variant={TextVariant.BodyStandard} tag={TextTag.Span} color={TextColor.Muted}>
                      |
                    </Text>
                    <Text variant={TextVariant.BodyStandard} tag={TextTag.Span} color={TextColor.Muted}>
                      {displayRoles}
                    </Text>
                  </>
                )}
              </span>
              <Icon className={styles.dropdownIcon} name={IconName.ChevronDown} size={AssetSize.XSmall} color={AssetColor.BrandPrimary} />
            </button>

            {isMenuOpen && <ProfileMenu name={auth.name} displayRoles={displayRoles} onProfile={handleClickOnProfile} onLogout={handleClickOnLogout} />}
          </div>
        )}
      </div>
    </header>
  )
}
